/**
 * TK-38 việc 6 (lee 23/10) — trang "Thống kê năng lượng": CÂY Tuyến → Tủ (+ tìm) và Ô THỐNG KÊ của mục chọn.
 *   Đọc `GET /api/dien-nang/thong-ke?tu=|tuyen=` (máy chủ gộp từ `MeterRollup` NGÀY, tháng/năm cộng từ ngày — `dienNangGop.ts`).
 *   Luật số 0: kỳ không tủ nào đo được ⇒ "—" và KHÔNG vẽ 0.
 *
 * KA-49 §5 (mẫu lee duyệt `mau_nang_luong`) — MỘT thành phần cho mọi phạm vi (toàn khu · tuyến · tủ · ĐÈN NEMA):
 *   · cây có kWh 30 ngày từng mục + cấp ĐÈN NEMA (kWh do máy chủ tích phân P — `nangLuongDen.ts`)
 *   · 5 ô số có đường xu hướng miền: Hôm nay · Hôm qua (so tuần trước) · N ngày · Công suất lúc này · Tiết kiệm nhờ dim (ước tính)
 *     — ô 5 CHỈ khi tính được từ dữ liệu thật (P lúc 100 % đo được), không thì "Số ngày có số đo"
 *   · kWh theo ngày: miền CHỒNG LỚP THEO TỦ (tuyến / toàn khu) · một tủ / một đèn ⇒ 1 lớp + đường TB 7 ngày; khung chú thích rê chuột
 *   · "Đêm qua · công suất theo lớp" (tủ · đèn NEMA, ô 15 phút) khi có số · bảng "Theo tủ và đèn" (kWh · tỉ lệ · Dim TB · nguồn)
 *   · giữ bảng kỳ (ngày / tháng / năm · tủ góp · tủ thiếu).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { locTuyen } from '../../lib/vanHanh';
import { trungBinhTruot } from '../../lib/mien';
import { lechGioMs } from '../../lib/giomaychu';
import { congTheoNgay, duoi, nguonSoLieu, soLieuNgay, tietKiemPhamVi, type DenNL, type DuLieuNL } from '../../lib/nangLuong';
import type { TreeRoute } from '../../lib/types';
import { ErrorBox, LOP_CHUA_RO, Spinner } from '../ui';
import { MienTheoKy, Spark, type LopMien } from '../bieudo/Mien';

export interface KyDien { ky: string; kwh: number | null; soTu: number; soTuThieu: number }
interface ThongKe { soTuPhamVi: number; ngay: KyDien[]; thang: KyDien[]; nam: KyDien[]; homNay: KyDien; homQua: KyDien; thangNay: KyDien; namNay: KyDien; theoTuNgay?: Record<string, (number | null)[]> }
export type ChonNL = { tuyen: string | null; tu: string | null; den?: number | null };
type Khoang = '7' | '30' | 'thang' | 'nam';
const NHAN_KHOANG: Record<Khoang, string> = { '7': '7 ngày', '30': '30 ngày', thang: 'Tháng', nam: 'Năm' };
const nhanKy = (t: 'ngay' | 'thang' | 'nam', ky: string) => (t === 'ngay' ? `${ky.slice(8, 10)}/${ky.slice(5, 7)}` : t === 'thang' ? `${ky.slice(5, 7)}/${ky.slice(0, 4)}` : ky);
const thamCua = (c: ChonNL) => (c.tu ? `tu=${c.tu}` : c.tuyen ? `tuyen=${c.tuyen}` : '');

export function useThongKe(tham: string) {
  return useQuery({ queryKey: ['dien-nang', 'thong-ke', tham] as const, queryFn: () => api.get<ThongKe>(`/api/dien-nang/thong-ke?${tham}`), refetchInterval: 5 * 60_000 });
}
export function useNangLuong(tham: string) {
  return useQuery({ queryKey: ['dien-nang', 'nang-luong', tham] as const, queryFn: () => api.get<DuLieuNL>(`/api/dien-nang/nang-luong?${tham}`), refetchInterval: 5 * 60_000 });
}

const kwhGon = (v: number | null) => (v === null ? '—' : fmtNum(v, v >= 100 ? 0 : 1));
const congSo = (ds: readonly (number | null)[]) => { const v = ds.filter((x): x is number => x !== null); return v.length ? v.reduce((s, x) => s + x, 0) : null; };
/** W ⇒ chữ gọn: ≥ 1000 W đổi kW. null ⇒ "—". */
const wGon = (w: number | null): { so: string; dv: string } => (w === null ? { so: '—', dv: '' } : w >= 1000 ? { so: fmtNum(w / 1000, 2), dv: 'kW' } : { so: fmtNum(w, 0), dv: 'W' });

/* ───────────── cây phạm vi ───────────── */

export function CayNangLuong({ cay, chon, onChon, kwhTu, den = [] }: {
  cay: TreeRoute[]; chon: ChonNL; onChon: (c: ChonNL) => void;
  /** KA-49: kWh 30 ngày theo tủ (toàn khu) — vắng ⇒ không hiện số */
  kwhTu?: Map<string, number | null>;
  /** KA-49: đèn NEMA (toàn khu) — cấp con của tủ */
  den?: DenNL[];
}) {
  const [q, setQ] = useState('');
  const [dong, setDong] = useState<Set<string>>(new Set());
  const ds = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return cay;
    const theoTuyen = new Set(locTuyen(cay, q).map((r) => r.id));
    return cay.flatMap((r) => theoTuyen.has(r.id) ? [r] : (r.cabinets.some((c) => c.name.toLowerCase().includes(k) || (c.code ?? '').toLowerCase().includes(k)) ? [{ ...r, cabinets: r.cabinets.filter((c) => c.name.toLowerCase().includes(k) || (c.code ?? '').toLowerCase().includes(k)) }] : []));
  }, [cay, q]);
  const lop = (sel: boolean) => `flex w-full min-w-0 items-center gap-1.5 rounded-nut px-2 py-1.5 text-left text-sm hover:bg-nen-2 ${sel ? 'bg-nen-2 ring-1 ring-nhan' : ''}`;
  const so = (v: number | null | undefined) => (kwhTu ? <span className="ml-auto shrink-0 pl-2 text-xs tabular-nums text-chu-2" data-o="kwh-cay">{v == null ? '—' : kwhGon(v)}</span> : null);
  const tongTuyen = (r: TreeRoute) => (kwhTu ? congSo(r.cabinets.map((c) => kwhTu.get(c.id) ?? null)) : null);
  const toanKhu = kwhTu ? congSo(cay.flatMap((r) => r.cabinets.map((c) => kwhTu.get(c.id) ?? null))) : null;
  const denChon = chon.den ?? null;
  return (
    <section className="khoi flex min-h-0 flex-col rounded-the border border-vien bg-nen-1" data-o="cay-nang-luong" aria-label="Tuyến và tủ">
      <div className="border-b border-vien p-2">
        <span className="block px-1 pb-1.5 text-[11px] tracking-wide text-chu-2">PHẠM VI{kwhTu ? ' · kWh 30 ngày' : ''}</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm tuyến, tủ" aria-label="Tìm tuyến, tủ"
          className="w-full rounded-nut border border-vien bg-nen-lom px-3 py-2 text-sm text-chu-1 placeholder:text-chu-3" />
      </div>
      <ul className="m-0 min-h-0 list-none overflow-y-auto p-1 lg:max-h-[calc(100vh-220px)]">
        <li><button type="button" aria-current={!chon.tuyen && !chon.tu ? 'true' : undefined} className={`muc-cay ${lop(!chon.tuyen && !chon.tu)}`} onClick={() => onChon({ tuyen: null, tu: null })} data-cay-toan-khu><span className="font-semibold text-chu-1">Toàn khu</span>{so(toanKhu)}</button></li>
        {ds.map((r) => {
          const mo = q.trim() !== '' || !dong.has(r.id);
          return (
            <li key={r.id}>
              <div className="flex items-center">
                <button type="button" aria-label={mo ? 'Thu' : 'Mở'} onClick={() => setDong((s) => { const n = new Set(s); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })} className="grid h-8 w-6 shrink-0 place-items-center text-chu-3">
                  {mo ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <button type="button" aria-current={chon.tuyen === r.id && !chon.tu ? 'true' : undefined} className={`muc-cay ${lop(chon.tuyen === r.id && !chon.tu)}`} onClick={() => onChon({ tuyen: r.id, tu: null })} data-cay-tuyen={r.id}>
                  <span className="min-w-0 flex-1 truncate font-semibold text-chu-1">{r.tenDayDu}</span>{so(tongTuyen(r))}
                </button>
              </div>
              {mo && (
                <ul className="m-0 list-none pl-7">
                  {r.cabinets.map((c) => {
                    const dsDen = den.filter((d) => d.cabinetId === c.id);
                    return (
                      <li key={c.id}>
                        <button type="button" aria-current={chon.tu === c.id && denChon === null ? 'true' : undefined} className={`muc-cay ${lop(chon.tu === c.id && denChon === null)}`} onClick={() => onChon({ tuyen: r.id, tu: c.id })} data-cay-tu={c.id}>
                          <span className={`h-2 w-2 shrink-0 rounded-full ${c.status === 'ONLINE' ? 'bg-tot' : 'bg-chu-3'}`} aria-hidden="true" />
                          <span className="min-w-0 flex-1 truncate text-chu-1">{c.name}</span>{so(kwhTu?.get(c.id) ?? null)}
                        </button>
                        {dsDen.length > 0 && (
                          <ul className="m-0 list-none pl-4">
                            {dsDen.map((d) => (
                              <li key={d.addr}>
                                <button type="button" aria-current={chon.tu === c.id && denChon === d.addr ? 'true' : undefined} className={`muc-cay ${lop(chon.tu === c.id && denChon === d.addr)} text-xs`}
                                  onClick={() => onChon({ tuyen: r.id, tu: c.id, den: d.addr })} data-cay-den={`${c.id}:${d.addr}`}>
                                  <span className="min-w-0 flex-1 truncate text-chu-2">{d.ten}</span>{so(d.kwh)}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <p className="m-0 border-t border-vien px-3 py-2.5 text-xs leading-relaxed text-chu-2">Đèn: app tự tính kWh từ công suất P mỗi lần quét. Tủ: lấy từ công tơ.</p>
    </section>
  );
}

/* ───────────── ô số ───────────── */

function OKpi({ nhan, so, dv, phu, ds, mau = '--nhan', lopSo = 'text-chu-1', lopPhu = 'text-chu-2', oDo }: {
  nhan: string; so: string; dv: string; phu: string; ds: (number | null)[]; mau?: string; lopSo?: string; lopPhu?: string; oDo: string;
}) {
  return (
    <section className="khoi relative min-w-0 overflow-hidden rounded-the border border-vien bg-nen-1 px-3.5 pb-10 pt-3" data-o={oDo}>
      <span className="block truncate text-xs text-chu-2">{nhan}</span>
      <strong className={`mt-1 block text-[26px] font-semibold leading-tight tabular-nums ${so === '—' ? 'text-chu-3' : lopSo}`}>
        {so}{so !== '—' && dv && <em className="ml-1 text-[13px] font-normal not-italic text-chu-2">{dv}</em>}
      </strong>
      <span className={`mt-0.5 block text-xs ${lopPhu}`}>{phu}</span>
      <div className="absolute inset-x-0 bottom-0"><Spark ds={ds} mau={mau} /></div>
    </section>
  );
}

/* ───────────── thống kê một phạm vi ───────────── */

export function OThongKe({ chon, ten }: { chon: ChonNL; ten: string }) {
  const tham = thamCua(chon);
  const laDen = chon.den != null && !!chon.tu;
  const [khoang, setKhoang] = useState<Khoang>('30');
  const q = useThongKe(tham);
  const nl = useNangLuong(tham);
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isLoading || !q.data) return <Spinner label="Đang tải…" />;
  const tk = q.data;
  const d = nl.data ?? null;
  const ngay = tk.ngay.map((k) => k.ky);
  /* căn chuỗi đèn (máy chủ trả theo `d.ngay`) vào đúng ngày của `tk.ngay` theo KHOÁ ngày — không theo vị trí */
  const viTri = new Map((d?.ngay ?? []).map((n, i) => [n, i]));
  const theoNgay = (ds: readonly (number | null)[]) => ngay.map((n) => { const i = viTri.get(n); return i === undefined ? null : ds[i] ?? null; });
  const denPV: DenNL[] = (d?.den ?? []).filter((x) => !laDen || (x.cabinetId === chon.tu && x.addr === chon.den));
  const denChon = laDen ? denPV[0] ?? null : null;
  const tong30: (number | null)[] = laDen ? theoNgay(denChon?.ngay ?? []) : tk.ngay.map((k) => k.kwh);
  const khoangHien: Khoang = laDen && (khoang === 'thang' || khoang === 'nam') ? '30' : khoang;
  const n = khoangHien === '7' ? 7 : 30;
  const so = soLieuNgay(tong30, n);
  const tkPV = tietKiemPhamVi(denPV);
  const tkNgay = theoNgay(congTheoNgay(denPV.map((x) => x.tkNgay), d?.ngay.length ?? 0));
  const tenTu = new Map((d?.tu ?? []).map((t) => [t.id, t.name]));
  const tuIds = laDen ? [] : Object.keys(tk.theoTuNgay ?? {});
  const nhieuTu = tuIds.length >= 2;

  /* dữ liệu biểu đồ */
  const tb7 = trungBinhTruot(tong30, 7);
  let duLieu: Record<string, unknown>[]; let lop: LopMien[]; let chong = false; let duongTb: { khoa: string; ten: string } | undefined;
  if (khoangHien === 'thang' || khoangHien === 'nam') {
    duLieu = tk[khoangHien].map((k) => ({ nhan: nhanKy(khoangHien, k.ky), kwh: k.kwh }));
    lop = [{ khoa: 'kwh', ten: 'kWh' }];
  } else {
    const tu0 = 30 - n;
    duLieu = ngay.slice(tu0).map((ky, j) => {
      const i = tu0 + j;
      const r: Record<string, unknown> = { nhan: nhanKy('ngay', ky), kwh: tong30[i], tb7: tb7[i], tk: tkNgay[i] };
      for (const id of tuIds) r[`t_${id}`] = tk.theoTuNgay?.[id]?.[i] ?? null;
      return r;
    });
    if (nhieuTu) {
      chong = true;
      lop = tuIds.map((id) => ({ khoa: `t_${id}`, ten: tenTu.get(id) ?? 'Tủ' }));
      if (tkPV) lop.push({ khoa: 'tk', ten: 'Tiết kiệm nhờ dim (ước tính)', mau: '--tim', gach: true });
    } else {
      lop = [{ khoa: 'kwh', ten: laDen ? denChon?.ten ?? 'Đèn' : ten }];
      duongTb = { khoa: 'tb7', ten: 'TB 7 ngày' };
    }
  }
  const coSoBieuDo = duLieu.some((r) => lop.some((l) => typeof r[l.khoa] === 'number'));

  /* công suất lúc này */
  const cs = d?.congSuat ?? null;
  const pDen = laDen ? denChon?.pNay ?? null : cs?.denW ?? null;
  const pChinh = laDen ? pDen : cs?.tuW ?? pDen;
  const wChinh = wGon(pChinh);
  const phuCs = laDen ? (pDen === null ? 'chưa có số đo mới (≤ 5 phút)' : 'số đo đèn mới nhất')
    : `tủ ${cs?.tuW == null ? '—' : `${wGon(cs.tuW).so} ${wGon(cs.tuW).dv}`} · đèn ${cs?.denW == null ? '—' : `${wGon(cs.denW).so} ${wGon(cs.denW).dv}`}${cs?.soTuChuaRoDonVi ? ` · ${cs.soTuChuaRoDonVi} tủ chưa rõ đơn vị` : ''}`;

  /* bảng theo tủ và đèn */
  const hangTu = tuIds.map((id) => ({ id, ten: tenTu.get(id) ?? 'Tủ', kwh: congSo(tk.theoTuNgay?.[id] ?? []), den: denPV.filter((x) => x.cabinetId === id) }));
  const nen = laDen ? (denChon?.kwh ?? null) : congSo(hangTu.map((h) => h.kwh)) ?? congSo(denPV.map((x) => x.kwh));
  const pct = (v: number | null) => (v === null || !nen ? null : (v / nen) * 100);
  const dimTb = (ds: DenNL[]) => { const v = ds.map((x) => x.dimTb).filter((x): x is number => x !== null); return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null; };
  const soNgayThieu = tong30.filter((x) => x === null).length;
  const dsKy = khoangHien === 'thang' || khoangHien === 'nam' ? tk[khoangHien] : tk.ngay.slice(30 - n);
  const loaiKy = khoangHien === 'thang' || khoangHien === 'nam' ? khoangHien : 'ngay';

  return (
    <div className="flex flex-col gap-3.5" data-o="o-thong-ke">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h2 className="m-0 truncate text-lg font-semibold text-chu-1">{laDen ? `${denChon?.ten ?? 'Đèn'} · ${tenTu.get(chon.tu!) ?? ''}` : ten}</h2>
          {!laDen && <small className="text-xs text-chu-2">{tk.soTuPhamVi} tủ trong phạm vi</small>}
        </div>
        <div className="nhom-lom ml-auto inline-flex gap-1 rounded-[10px] p-1" role="group" aria-label="Khoảng thời gian">
          {(laDen ? (['7', '30'] as const) : (['7', '30', 'thang', 'nam'] as const)).map((k) => (
            <button key={k} type="button" aria-pressed={khoangHien === k} onClick={() => setKhoang(k)} data-khoang={k}
              className={`rounded-[7px] px-3.5 py-1.5 text-[13px] ${khoangHien === k ? 'text-nhan' : 'text-chu-1 hover:text-nhan-sang'}`}>{NHAN_KHOANG[k]}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-o="o-so-nang-luong">
        <OKpi oDo="kwh-hom-nay" nhan="Hôm nay" so={kwhGon(so.homNay)} dv="kWh" phu={laDen ? 'tích phân P của đèn' : `${tk.homNay.soTu} tủ góp số`} ds={duoi(tong30, 9)} />
        <OKpi oDo="kwh-hom-qua" nhan="Hôm qua" so={kwhGon(so.homQua)} dv="kWh" mau="--xanh-ngoc"
          phu={so.chenhPct === null ? `tuần trước ${kwhGon(so.tuanTruoc)}` : `${so.chenhPct <= 0 ? 'thấp' : 'cao'} hơn tuần trước ${fmtNum(Math.abs(so.chenhPct), 0)} %`}
          lopPhu={so.chenhPct === null ? 'text-chu-2' : 'text-xanh-ngoc'} ds={duoi(tong30, 8).slice(0, 7)} />
        <OKpi oDo="kwh-tong" nhan={`${n} ngày`} so={kwhGon(so.tongN)} dv="kWh" phu={`${so.soNgayCo} / ${so.soNgay} ngày có số`} ds={duoi(tong30, n)} />
        <OKpi oDo="cong-suat-nay" nhan="Công suất lúc này" so={wChinh.so} dv={wChinh.dv} phu={phuCs} ds={d?.demQua?.denW ?? []} />
        {tkPV
          ? <OKpi oDo="tiet-kiem" nhan="Tiết kiệm nhờ dim (ước tính)" so={kwhGon(tkPV.kwh)} dv="kWh" mau="--tim" lopSo="text-tim" lopPhu="text-tim"
              phu={`${tkPV.pct === null ? '' : `ước tính ${fmtNum(tkPV.pct, 0)} % · `}30 ngày · ${tkPV.soDenTinh}/${tkPV.soDen} đèn`} ds={duoi(tkNgay, 10)} />
          : <OKpi oDo="so-ngay-co-so" nhan="Số ngày có số đo" so={String(tong30.filter((x) => x !== null).length)} dv="/ 30" phu="ngày thiếu số không tính là 0" ds={[]} />}
      </div>

      <section className="khoi rounded-the border border-vien bg-nen-1 p-4" data-o="do-thi-thong-ke">
        <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <h3 className="m-0 text-sm font-semibold text-chu-1">kWh theo {loaiKy === 'ngay' ? `ngày · ${n} ngày` : NHAN_KHOANG[khoangHien].toLowerCase()} · {laDen ? denChon?.ten ?? 'Đèn' : ten}</h3>
          <div className="ml-auto flex flex-wrap gap-x-3 gap-y-1 text-xs text-chu-2" data-o="chu-giai-lop">
            {lop.map((l, i) => (
              <span key={l.khoa} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-[3px]" style={{ background: `linear-gradient(180deg, rgb(var(${l.mau ?? ['--nhan', '--xanh-ngoc', '--tim', '--nhan-dam', '--anh'][i % 5]})), transparent)`, border: l.gach ? '1px dashed rgb(var(--tim))' : undefined }} />{l.ten}</span>
            ))}
            {duongTb && <span className="flex items-center gap-1.5"><span className="h-0 w-3.5 border-t-2 border-dashed border-tim" />{duongTb.ten}</span>}
          </div>
        </div>
        {!coSoBieuDo ? <p className={`m-0 py-12 text-center text-sm ${LOP_CHUA_RO}`}>Chưa có số đo</p>
          : <MienTheoKy ds={duLieu} truc="nhan" lop={lop} chong={chong} donVi="kWh" duongTb={duongTb} cao={240} oDo="mien-kwh" />}
        <p className="m-0 mt-2 text-xs text-chu-2">Ngày thiếu số đo để trống, không tính là 0.{chong && tkPV ? ' Phần tiết kiệm so với chạy 100 % (P lúc 100 % đo được của từng đèn).' : ''}</p>
      </section>

      <div className={`grid grid-cols-1 items-start gap-3.5 ${!laDen && d?.demQua ? 'xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]' : ''}`}>
        {!laDen && d?.demQua && (() => {
          const dq = d.demQua;
          const t0 = new Date(dq.tu).getTime();
          const coTu = dq.tuW.some((x) => x !== null);
          /* đêm đang chạy: chỉ tới ô hiện tại — phần CHƯA TỚI không phải "thiếu số" (không tô gạch) */
          const tNay = Date.now() + (lechGioMs() ?? 0);
          const rows = dq.denW.map((v, i) => { const t = new Date(t0 + i * 15 * 60_000); return { ms: t.getTime(), nhan: `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`, tu: dq.tuW[i], den: v }; })
            .filter((r) => r.ms <= tNay);
          const lopDem: LopMien[] = [...(coTu ? [{ khoa: 'tu', ten: 'Tủ' }] : []), { khoa: 'den', ten: 'Đèn', mau: '--xanh-ngoc' }];
          return (
            <section className="khoi min-w-0 rounded-the border border-vien bg-nen-1 p-4" data-o="dem-qua">
              <h3 className="m-0 mb-2 text-sm font-semibold text-chu-1">{dq.dangChay ? 'Đêm nay (đang chạy)' : 'Đêm qua'} · công suất theo lớp</h3>
              <MienTheoKy ds={rows} truc="nhan" lop={lopDem} donVi="W" soLe={0} cao={200} />
              {!coTu && dq.soTuChuaRoDonVi > 0 && <p className="m-0 mt-1 text-xs text-chu-2">Lớp tủ: {dq.soTuChuaRoDonVi} tủ chưa rõ đơn vị công tơ — không vẽ.</p>}
            </section>
          );
        })()}

        <section className="khoi min-w-0 overflow-hidden rounded-the border border-vien bg-nen-1" data-o="bang-tu-den">
          <div className="border-b border-vien px-4 py-3"><h3 className="m-0 text-sm font-semibold text-chu-1">Theo tủ và đèn · 30 ngày</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] border-collapse text-[13px] tabular-nums">
              <thead><tr className="op-dau-bang text-left text-chu-2">
                <th className="px-4 py-2 font-medium">Đối tượng</th><th className="px-2 py-2 font-medium">kWh</th><th className="px-2 py-2 font-medium">Tỉ lệ</th>
                <th className="px-2 py-2 font-medium">Dim TB</th><th className="px-4 py-2 font-medium">Số liệu</th>
              </tr></thead>
              <tbody>
                {(laDen ? [{ id: chon.tu!, ten: tenTu.get(chon.tu!) ?? 'Tủ', kwh: null as number | null, den: denPV, an: true }] : hangTu.map((h) => ({ ...h, an: false }))).map((h) => (
                  <HangTu key={h.id} ten={h.ten} kwh={h.kwh} pct={pct(h.kwh)} dim={dimTb(h.den)} an={h.an}>
                    {h.den.map((x) => <HangDen key={x.addr} d={x} pct={pct(x.kwh)} />)}
                  </HangTu>
                ))}
                {soNgayThieu > 0 && (
                  <tr className="border-t border-vien text-chu-2" data-o="dong-thieu-so"><td className="px-4 py-2">Thiếu số đo</td><td className="px-2 py-2">{soNgayThieu} ngày</td><td /><td className="px-2 py-2">—</td><td className="px-4 py-2" /></tr>
                )}
                {!laDen && hangTu.length === 0 && <tr><td colSpan={5} className="px-4 py-4 text-center text-chu-3">Chưa có tủ trong phạm vi</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="khoi overflow-x-auto rounded-the border border-vien bg-nen-1">
        <table className="w-full border-collapse text-xs" data-o="bang-thong-ke">
          <thead><tr className="op-dau-bang text-chu-2">
            <th className="px-3 py-2 text-left font-normal">{loaiKy === 'ngay' ? 'Ngày' : NHAN_KHOANG[khoangHien]}</th><th className="px-3 py-2 text-right font-normal">kWh</th>
            {!laDen && <><th className="px-3 py-2 text-right font-normal">Tủ góp số</th><th className="px-3 py-2 text-right font-normal">Tủ thiếu số</th></>}
          </tr></thead>
          <tbody>
            {(laDen ? ngay.slice(30 - n).map((ky, j) => ({ ky, kwh: tong30[30 - n + j], soTu: 0, soTuThieu: 0 })) : dsKy).slice().reverse().map((k) => (
              <tr key={k.ky} className="border-t border-vien text-chu-1">
                <td className="px-3 py-2">{nhanKy(loaiKy, k.ky)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${k.kwh === null ? 'text-chu-3' : ''}`}>{k.kwh === null ? '—' : fmtNum(k.kwh, 1)}</td>
                {!laDen && <><td className="px-3 py-2 text-right tabular-nums">{k.soTu}</td><td className={`px-3 py-2 text-right tabular-nums ${k.soTuThieu ? 'text-chu-2' : 'text-chu-3'}`}>{k.soTuThieu}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ThanhTiLe({ pct }: { pct: number | null }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block h-2 w-[52px] overflow-hidden rounded bg-nen-lom shadow-[inset_0_1px_2px_rgb(0_0_0/0.5)]">
        {pct !== null && <span className="block h-2 rounded" style={{ width: `${Math.min(100, pct)}%`, background: 'linear-gradient(180deg, rgb(var(--nhan-sang)), rgb(var(--nhan-dam)))' }} />}
      </span>
      <span className="whitespace-nowrap text-chu-2">{pct === null ? '—' : `${fmtNum(pct, 0)} %`}</span>
    </div>
  );
}

function NhanNguon({ loai, src }: { loai: 'tu' | 'den'; src: number | null }) {
  const n = nguonSoLieu(loai, src);
  const lop = n.tone === 'ngoc' ? 'bg-xanh-ngoc/15 text-xanh-ngoc' : n.tone === 'canh' ? 'bg-canh-nen text-canh' : 'bg-nen-2 text-chu-2';
  return <span className={`rounded-full px-2 py-0.5 text-xs ${lop}`}>{n.nhan}</span>;
}

function HangTu({ ten, kwh, pct, dim, an, children }: { ten: string; kwh: number | null; pct: number | null; dim: number | null; an: boolean; children: ReactNode }) {
  return (
    <>
      {!an && (
        <tr className="border-t border-vien" data-o="hang-tu">
          <td className="px-4 py-2 font-semibold text-chu-1">{ten}</td><td className="px-2 py-2">{kwhGon(kwh)}</td><td className="px-2 py-2"><ThanhTiLe pct={pct} /></td>
          <td className="px-2 py-2">{dim === null ? '—' : `${fmtNum(dim, 0)} %`}</td><td className="px-4 py-2"><NhanNguon loai="tu" src={null} /></td>
        </tr>
      )}
      {children}
    </>
  );
}

function HangDen({ d, pct }: { d: DenNL; pct: number | null }) {
  return (
    <tr className="border-t border-vien" data-o="hang-den">
      <td className="py-2 pl-8 pr-2 text-chu-1">{d.ten}{d.rated_w != null ? ` · ${d.rated_w} W` : ''}</td><td className="px-2 py-2">{kwhGon(d.kwh)}</td><td className="px-2 py-2"><ThanhTiLe pct={pct} /></td>
      <td className="px-2 py-2">{d.dimTb === null ? '—' : `${fmtNum(d.dimTb, 0)} %`}</td><td className="px-4 py-2"><NhanNguon loai="den" src={d.src} /></td>
    </tr>
  );
}
